import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@taxdown/zeno-sama-query';
import TaxService from 'Services/tax';
import { PueblaPreviousYearsResponse, SatResponseType } from 'Services/sat/types';
import SatService from 'Services/sat';
import PreviousYearsService from 'Services/previousYears';
import CheckoutService from 'Services/checkout';
import RecordsService from 'Services/records';
import { RequestPreviousYearResponse } from 'Services/previousYears/types';
import { useGetQuery } from 'Hooks/useGetQuery';
import { stages } from 'Constants/stages';
import { FRONT_PLANS, PLAN_FLEX_LITE, plansNameMapping } from 'Constants/plans';
import {
  CustomerType,
  FlexInfoResponse,
  invoiceFlexResponse,
  QueryKeys,
  ReintentType,
  ResponseArrayType,
  ResponseType,
  ResultType,
  ResultTypes,
  SeasonFiltered,
  SeasonType,
  SpeiOutResponse,
  UserChargementsResponse,
} from 'Types/common';
import { convertToCurrency } from './components/TaxScreen/components/ResultInfo/utils';

const formatRecord = ({ id, ...rest }: { id: string; rest }): { recordId: string; rest } => ({
  recordId: id,
  ...rest,
});

export const getUserLastStage = (filing: SeasonFiltered): string => {
  const lastStage = filing?.filingInfo?.recent ? filing?.filingInfo?.recent?.status : '';
  return lastStage;
};

export const seasonsSelector = (e: ResponseArrayType<SeasonType>): SeasonFiltered[] => {
  const seasons = e.data.map(item => item.item);
  const seasonsFormatted = seasons?.map(({ records, ...rest }) => ({
    ...rest,
    stage: stages.DATA_RETRIEVAL,
    ...(records && records.length > 0 && formatRecord(records[0] as { id: string; rest })),
  }));
  const seasonsFiltered = seasonsFormatted?.filter(
    ({ isSeasonOver, recordId, enabled }) => (isSeasonOver && recordId) || !isSeasonOver || enabled
  );
  const seasonsFilteredWithStage = seasonsFiltered?.map(item => ({
    ...item,
    stage:
      item.stage === stages.SUBMITTED_CHANGED_PAUSED_REMINDERS
        ? getUserLastStage(item)
        : item.stage,
  }));
  return seasonsFilteredWithStage;
};

export const useFetchSeasons = (): UseQueryResult<SeasonFiltered[]> =>
  useQuery([QueryKeys.FETCH_SEASONS], TaxService.getSeasons, {
    select: data => seasonsSelector(data),
    cacheTime: 0,
  });

export const useGetUserChargements = (
  recordId: string
): UseQueryResult<ResponseType<UserChargementsResponse>, unknown> =>
  useQuery([QueryKeys.GET_USER_CHARGEMENTS], () => CheckoutService.getUserChargements(recordId), {
    enabled: !!recordId,
  });

const getUserDataSelector = (
  data: ResponseType<PueblaPreviousYearsResponse>
): PueblaPreviousYearsResponse => {
  const userData = data.data;
  return userData.item;
};
export const useFetchPreviousYear = (): UseQueryResult<PueblaPreviousYearsResponse> =>
  useQuery(
    [QueryKeys.FETCH_PREVIOUS_YEARS],
    (): Promise<ResponseType<PueblaPreviousYearsResponse>> => SatService.getPueblaPreviousYears(),
    {
      select: data => getUserDataSelector(data),
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );
export const useFetchPreviousYearIsRequest = (): UseQueryResult<RequestPreviousYearResponse> =>
  useQuery(
    [QueryKeys.FETCH_PREVIOUS_YEARS_REQUEST],
    PreviousYearsService.getUserRequestPreviousYear,
    {
      select: data => {
        const response = data?.data;
        return response?.item;
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );

export const useGetUserFlexData = (): invoiceFlexResponse => {
  const data = useGetQuery([
    QueryKeys.GET_PLAN_FLEX_INVOICE,
  ]) as ResponseArrayType<invoiceFlexResponse>;
  const info = data?.data;
  const lastInvoice = info?.[0]?.item;

  return {
    ...lastInvoice,
    amountDue: convertToCurrency(lastInvoice?.amountDue),
    baseServiceAmount: convertToCurrency(lastInvoice?.baseServiceAmount),
  };
};

export const useGetUserSpeiOut = (isFlex: boolean): UseQueryResult<SpeiOutResponse> => {
  const searchParamsGeneral = new URLSearchParams();
  searchParamsGeneral.set('sort', '-created');
  searchParamsGeneral.set(`filter[status][eq]`, 'settled');
  return useQuery(
    [QueryKeys.GET_SPEI_OUTS],
    () => CheckoutService.getUserSpeiOut(searchParamsGeneral),
    {
      select: data => {
        const response = data.data;
        return response[0].item;
      },
      enabled: isFlex,
    }
  );
};

export const useConfirmDeclaration = (): UseMutationResult<unknown> => {
  const queryClient = useQueryClient();
  return useMutation([QueryKeys.CONFIRM_DECLARATION], RecordsService.confirmDeclaration, {
    onSuccess: () => {
      queryClient.resetQueries([QueryKeys.FETCH_SEASONS]);
    },
  });
};

export const useGetFlexInvoices = (): UseQueryResult<invoiceFlexResponse> =>
  useQuery([QueryKeys.GET_PLAN_FLEX_INVOICE], () => CheckoutService.getFlexInvoice());

export const useGetUserFlexInfo = (): FlexInfoResponse => {
  const data = useGetQuery([QueryKeys.GET_PLAN_FLEX_INFO]) as ResponseType<FlexInfoResponse>;
  const info = data?.data;

  return {
    ...info?.item,
    discountedPrice: convertToCurrency(info?.item?.discountedPrice),
    price: convertToCurrency(info?.item?.price),
  };
};

export const useFetchPreviousYearCacheInfo = (): PueblaPreviousYearsResponse | undefined => {
  const response = useGetQuery<ResponseType<PueblaPreviousYearsResponse>>([
    QueryKeys.FETCH_PREVIOUS_YEARS,
  ]);
  const data = response?.data;
  return data?.item;
};

export const useFetchSatUserCacheInfo = (): SatResponseType | undefined => {
  const response = useGetQuery<ResponseType<SatResponseType>>([QueryKeys.USER_SAT_INFO]);
  const data = response?.data;
  return data?.item;
};

export const useGetUserWithPreFiling = (
  userStage: string | null,
  taxReturnResult?: ResultType
): boolean => {
  const data = useGetQuery<ResponseType<CustomerType>>([QueryKeys.FETCH_GET_CUSTOMER]);
  const info = data?.data;
  const customer = info?.item;
  const plan =
    customer && customer.activePlan
      ? customer.activePlan.name === PLAN_FLEX_LITE
        ? FRONT_PLANS.FLEX_LITE
        : plansNameMapping(customer.activePlan.name)
      : FRONT_PLANS.BASIC;
  const userInfo =
    userStage === stages.RESULT &&
    !taxReturnResult?.mandatory &&
    plan === FRONT_PLANS.FLEX_LITE &&
    taxReturnResult?.taxReturnType !== ResultTypes.toReturn;

  const usersMandatoryAndToPay =
    userStage === stages.RESULT &&
    Boolean(taxReturnResult?.mandatory) &&
    plan === FRONT_PLANS.FLEX_LITE &&
    taxReturnResult?.taxReturnType === ResultTypes.toPay;

  return userInfo || usersMandatoryAndToPay;
};

export const useGetQueryUserIsOnReintent = (): ReintentType | undefined => {
  const response = useGetQuery<ResponseType<ReintentType>>([QueryKeys.REINTENTS]);
  const data = response?.data;
  return data?.item;
};
